import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@calendly/ui/components/dropdown-menu';
import { IconChevronDown } from '@calendly/ui/components/glyphs/IconChevronDown';
import { IconChevronUp } from '@calendly/ui/components/glyphs/IconChevronUp';
import { IconLink } from '@calendly/ui/components/glyphs/IconLink';
import { IconLock } from '@calendly/ui/components/glyphs/IconLock';
import { IconUsers } from '@calendly/ui/components/glyphs/IconUsers';
import { BodyText, LabelText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { colorTextSecondary } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef, useState } from 'react';
import { normalizeRecapAccessLevel, RECAP_ACCESS_LEVELS, RecapPermission } from 'features/recaps/constants';
import { useRecapShare } from 'features/recaps/meeting_list/meeting_card/recap_share_context';
const getTitle = accessLevel => ({
  [RECAP_ACCESS_LEVELS.RESTRICTED]: I18n.t('publisher.meeting_recap.share_modal.access_level.restricted.title'),
  [RECAP_ACCESS_LEVELS.PRIVATE]: I18n.t('publisher.meeting_recap.share_modal.access_level.private.title'),
  [RECAP_ACCESS_LEVELS.PUBLIC]: I18n.t('publisher.meeting_recap.share_modal.access_level.public.title')
})[normalizeRecapAccessLevel(accessLevel)];
const getSubheader = accessLevel => ({
  [RECAP_ACCESS_LEVELS.RESTRICTED]: I18n.t('publisher.meeting_recap.share_modal.access_level.restricted.subheader'),
  [RECAP_ACCESS_LEVELS.PRIVATE]: I18n.t('publisher.meeting_recap.share_modal.access_level.private.subheader'),
  [RECAP_ACCESS_LEVELS.PUBLIC]: I18n.t('publisher.meeting_recap.share_modal.access_level.public.subheader')
})[normalizeRecapAccessLevel(accessLevel)];
const ACCESS_LEVEL_GLYPHS = {
  [RECAP_ACCESS_LEVELS.PRIVATE]: IconLock,
  [RECAP_ACCESS_LEVELS.RESTRICTED]: IconUsers,
  [RECAP_ACCESS_LEVELS.PUBLIC]: IconLink
};
const rowCss = "r1aff9sg";
const Row = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(rowCss, props.className)} />;
});
const textContainerCss = "t1mhkj9y";
const TextContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(textContainerCss, props.className)} />;
});
const dropdownContainerCss = "dat2vkc";
const accessLevelMenuContainerCss = "a1nfdicr";
const accessLevelTriggerButtonCss = "a1keizew";
const accessLevelReadOnlyLabelCss = "a1wag4wu";
const menuItemCss = "mx1shbk";
const MenuItem = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(menuItemCss, props.className)} />;
});
const iconContainerCss = "i1x891sj";
const IconContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(iconContainerCss, props.className)} />;
});
const menuItemTextCss = "mi62zik";
const MenuItemText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(menuItemTextCss, props.className)} />;
});
const DropdownContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(dropdownContainerCss, props.className)} />;
});
const AccessLevelSelect = ({
  value,
  onChange
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const handleToggleDropdown = () => {
    setIsDropdownOpen(open => !open);
  };
  const handleSelect = selectedAccessLevel => {
    setIsDropdownOpen(false);
    if (selectedAccessLevel !== value) {
      onChange(selectedAccessLevel);
    }
  };
  const chevronIcon = isDropdownOpen ? IconChevronUp : IconChevronDown;
  return <DropdownContainer>
      <div className={accessLevelMenuContainerCss}>
        <DropdownMenu isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)}>
          <DropdownMenuTrigger>
            <Button decoration="ghost" size="medium" className={accessLevelTriggerButtonCss} onClick={handleToggleDropdown} aria-label={I18n.t('publisher.meeting_recap.share_modal.access_level.aria_label')} aria-expanded={isDropdownOpen} aria-haspopup="menu" data-testid="access-level-select-trigger">
              {getTitle(value)}
              <UiIcon icon={chevronIcon} size="small" title="" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent placement="bottom end" data-testid="access-level-select-menu">
            {Object.values(RECAP_ACCESS_LEVELS).map(accessLevel => {
            if (accessLevel === RECAP_ACCESS_LEVELS.PASSCODE) {
              return null;
            }
            const accessLevelGlyph = ACCESS_LEVEL_GLYPHS[accessLevel];
            return <DropdownMenuItem key={accessLevel} onSelect={() => handleSelect(accessLevel)} data-testid={`access-level-option-${accessLevel}`}>
                  <MenuItem>
                    <IconContainer>
                      <UiIcon icon={accessLevelGlyph} size="small" title="" />
                    </IconContainer>
                    <MenuItemText>
                      <BodyText weight="bold">
                        {getTitle(accessLevel)}
                      </BodyText>
                      <BodyText color="muted">
                        {getSubheader(accessLevel)}
                      </BodyText>
                    </MenuItemText>
                  </MenuItem>
                </DropdownMenuItem>;
          })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </DropdownContainer>;
};
const AccessLevelReadOnly = () => <DropdownContainer>
    <LabelText size="large" color={colorTextSecondary} className={accessLevelReadOnlyLabelCss} data-testid="access-level-select-trigger">
      {I18n.t('publisher.meeting_recap.share_modal.access_level.restricted.title')}
    </LabelText>
  </DropdownContainer>;
export const AccessLevelForm = () => {
  const {
    recapPermission,
    accessLevel: {
      value: accessLevel,
      onChange: handleAccessLevelChange
    }
  } = useRecapShare();
  const canChangeAccessLevel = recapPermission === RecapPermission.OWNER;
  return <Row>
      <TextContainer>
        <BodyText weight="bold" size="large">
          {I18n.t('publisher.meeting_recap.share_modal.access_level.header')}
        </BodyText>
        <BodyText color="muted">
          {getSubheader(accessLevel)}
        </BodyText>
      </TextContainer>
      {canChangeAccessLevel ? <AccessLevelSelect value={accessLevel} onChange={handleAccessLevelChange} /> : <AccessLevelReadOnly />}
    </Row>;
};

require("./access_level_form.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./access_level_form.js");